import type { LogFn } from '../../log.js';
import type { CloseableBridgeState } from '../close-bridge-connection.js';
import type { MainBridgeReconnectState, FirehoseReconnectContext } from '../reconnect/index.js';
import {
  beginFirehoseDeferredDisconnect,
  clearFirehoseReconnectQuietOnOpen,
} from '../reconnect/index.js';

export interface FirehoseConnectCallbacks {
  onOpen: () => void;
  onClose: (code: number, reason: string) => void;
}

export function createFirehoseConnectCallbacks(
  state: MainBridgeReconnectState & CloseableBridgeState,
  myGen: number,
  logFn: LogFn,
  scheduleFirehoseRetryAfterDrop: (closeMeta?: { code: number; reason: string }) => void,
): FirehoseConnectCallbacks {
  function firehoseCtx(): FirehoseReconnectContext {
    return {
      closedByUser: state.closedByUser,
      currentWs: state.currentWs,
      firehoseHandle: state.firehoseHandle,
      firehoseQuiet: state.firehoseQuiet,
    };
  }

  return {
    onOpen: () => {
      if (myGen !== state.firehoseGeneration) return;
      try {
        clearFirehoseReconnectQuietOnOpen(
          {
            firehoseQuiet: state.firehoseQuiet,
            firehoseOutage: state.firehoseOutage,
            lastFirehoseReconnectCloseMeta: state.lastFirehoseReconnectCloseMeta,
          },
          logFn,
        );
      } catch {
        /* ignore */
      }
      const logOpenAsFirehoseReconnect = state.firehoseReconnectAttempt > 0;
      state.firehoseReconnectAttempt = 0;
      if (!logOpenAsFirehoseReconnect) {
        try {
          logFn('Connected to preview tunnel (local HTTP proxy and dev logs).');
        } catch {
          /* ignore */
        }
      }
    },
    onClose: (code, reason) => {
      if (myGen !== state.firehoseGeneration) return;
      state.firehoseHandle = null;
      if (state.closedByUser) return;
      state.lastFirehoseReconnectCloseMeta = { code, reason };
      try {
        beginFirehoseDeferredDisconnect(firehoseCtx(), code, reason, logFn);
      } catch {
        /* ignore */
      }
      try {
        scheduleFirehoseRetryAfterDrop({ code, reason });
      } catch {
        /* ignore */
      }
    },
  };
}
